import { GlobalService } from './globals';

/** All Category Head TCB approval rules — single place to review logic. */

export const TCB_CH_AUTH_CODE = 'TCB_CATEGORY_HEAD_APPROVAL';
export let CH_MIN_GP_PER = 0;

/** Set approve GM/month threshold from API (`ch_min_gp_per`). */
export function applyChMinGpPerFromApi(value: unknown): void {
  const v = parseFloat(String(value ?? ''));
  if (!isNaN(v)) {
    CH_MIN_GP_PER = v;
  }
}
/** TCB module enabled: Direct and/or FinTech RM trade access. */
export function isTcbModuleEnabled(globals: GlobalService): boolean {
  return globals.mflag_tcb_access_trade_direct === true
    || globals.mflag_tcb_access_trade_fintech === true;
}

/** Category Head approval menu + screens. */
export function hasCategoryHeadApprovalAuth(globals: GlobalService): boolean {
  return globals.mflag_tcb_category_head_approval === true;
}

/** Side menu: both TCB access and CH approval auth. */
export function showCategoryHeadTcbMenu(globals: GlobalService): boolean {
  return isTcbModuleEnabled(globals) && hasCategoryHeadApprovalAuth(globals);
}

/**
 * Approve enabled when Gross Margin / Month (%) is at least 2.5% (farm tr_tcb.js).
 */
export function canShowApprovalActions(gpPerMonth: number | string | null | undefined): boolean {
  const gp = parseFloat(String(gpPerMonth ?? ''));
  return !isNaN(gp) && gp >= CH_MIN_GP_PER;
}

/** Format GM % for display (includes zero and negative; NaN → 0.00). */
export function formatGpPercentDisplay(value: number | string | null | undefined): string {
  const n = parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

export function approvalActionsDisabledReason(gpPerMonth: number | string | null | undefined): string {
  if (canShowApprovalActions(gpPerMonth)) {
    return '';
  }
  const gp = parseFloat(String(gpPerMonth ?? ''));
  const current = !isNaN(gp) ? ` (current: ${gp.toFixed(2)}%)` : '';
  return `Approve needs Gross Margin / Month (%) at least ${CH_MIN_GP_PER}%${current}.`;
}

/** GM% fields from API rows (number, string, or absent). */
function coerceGpMetric(value: unknown): number | string | null | undefined {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return undefined;
}

/** Resolve transaction GM % from getTCBDetails / CH list row (same fallback as CH gate). */
export function resolveGpPerFromTcb(data: Record<string, unknown> | null | undefined): number {
  if (!data) {
    return 0;
  }
  const gpCalc = data['gp_calc'] as { gp_per?: number | string | null } | null | undefined;
  const buyerOrResolved = coerceGpMetric(
    data['gp_per'] ??
      data['gross_margin_per'] ??
      (data['actuals_summary'] as Record<string, unknown> | undefined)?.['planned_gp_percent'],
  );
  return resolveChGpPerForGate(buyerOrResolved, gpCalc);
}

/** Resolve GM/month from getTCBDetails / CH list (buyer month when > 0, else gp_calc / enriched gp_per_month). */
export function resolveGpPerMonthFromTcb(
  data: Record<string, unknown> | null | undefined,
): number {
  if (!data) {
    return 0;
  }
  const gpCalc = data['gp_calc'] as { gp_per_month?: number | string | null } | null | undefined;
  const buyerMonth = coerceGpMetric(data['gross_margin_per_month']);
  const resolvedMonth = coerceGpMetric(data['gp_per_month']);
  return resolveChGpPerMonthForDisplay(buyerMonth, {
    gp_per_month: resolvedMonth ?? gpCalc?.gp_per_month,
  });
}

/**
 * CH approve gate: tr_tcb_buyer.gross_margin_per, else active tr_tcb_gp_value.gp_per.
 * (Buyer snapshot can be 0 while GP modal shows gp_calc.)
 */
export function resolveChGpPerForGate(
  buyerGpPer: number | string | null | undefined,
  gpCalc?: { gp_per?: number | string | null } | null
): number {
  const buyer = parseFloat(String(buyerGpPer ?? ''));
  if (!isNaN(buyer) && buyer > 0) {
    return buyer;
  }
  const calc = parseFloat(String(gpCalc?.gp_per ?? ''));
  return !isNaN(calc) ? calc : 0;
}

/**
 * CH approve gate: prefer gp_calc.gp_per_month (GP modal), else buyer gross_margin_per_month.
 */
export function resolveChGpPerMonthForGate(
  buyerGpPerMonth: number | string | null | undefined,
  gpCalc?: { gp_per_month?: number | string | null } | null,
): number {
  const calc = parseFloat(String(gpCalc?.gp_per_month ?? ''));
  if (!isNaN(calc) && calc > 0) {
    return calc;
  }
  const buyer = parseFloat(String(buyerGpPerMonth ?? ''));
  if (!isNaN(buyer) && buyer > 0) {
    return buyer;
  }
  return !isNaN(calc) ? calc : 0;
}

/** Resolve GM/month for CH approve gate from API row / GP modal data. */
export function resolveGpPerMonthForGateFromTcb(
  data: Record<string, unknown> | null | undefined,
): number {
  if (!data) {
    return 0;
  }
  const gpCalc = data['gp_calc'] as { gp_per_month?: number | string | null } | null | undefined;
  const buyerMonth = coerceGpMetric(data['gross_margin_per_month']);
  const resolvedMonth = coerceGpMetric(data['gp_per_month']);
  return resolveChGpPerMonthForGate(buyerMonth, {
    gp_per_month: resolvedMonth ?? gpCalc?.gp_per_month,
  });
}

/**
 * CH list/details display: buyer gross_margin_per_month when > 0, else tr_tcb_gp_value.gp_per_month.
 */
export function resolveChGpPerMonthForDisplay(
  buyerGpPerMonth: number | string | null | undefined,
  gpCalc?: { gp_per_month?: number | string | null } | null
): number {
  const buyer = parseFloat(String(buyerGpPerMonth ?? ''));
  if (!isNaN(buyer) && buyer > 0) {
    return buyer;
  }
  const calc = parseFloat(String(gpCalc?.gp_per_month ?? ''));
  return !isNaN(calc) ? calc : 0;
}

/** Status badge color for list / header. */
export function statusColor(status: string): string {
  switch (status) {
    case 'Pending Approval':
    case 'Reapproval Pending':
      return 'warning';
    case 'TCB Approved':
      return 'success';
    case 'TCB Rejected':
      return 'danger';
    case 'TCB Revised':
      return 'medium';
    case 'TCB Expired':
      return 'warning';
    case 'TCB Closed':
      return 'dark';
    default:
      return 'primary';
  }
}

/** Approve/Reject/Revise only on pending publish states (mirrors farm). */
export function canActOnTcbStatus(tcbStatus: string): boolean {
  return tcbStatus === 'Pending Approval' || tcbStatus === 'Reapproval Pending';
}

/** Client-side list tabs only (valid_till / expired filtering is on getTCBApprovalListForCH API). */
export function filterChListByStatusSegment(
  rows: any[],
  segment: 'pending' | 'approved' | 'revised' | 'rejected' | 'all' | string,
): any[] {
  if (!rows?.length || segment === 'all') {
    return rows ?? [];
  }
  const status = (s: string) => String(s ?? '').trim();
  return rows.filter((row) => {
    const st = status(row.tcb_status);
    switch (segment) {
      case 'pending':
        return st === 'Pending Approval' || st === 'Reapproval Pending';
      case 'approved':
        return st === 'TCB Approved';
      case 'revised':
        return st === 'TCB Revised';
      case 'rejected':
        return st === 'TCB Rejected';
      default:
        return true;
    }
  });
}

/** Same order as getTCBApprovalListForCH / farm list CASE. */
export function resolveTcbStatusFromFlags(tcb: Record<string, unknown> | null | undefined): string {
  if (!tcb) {
    return '';
  }
  const flagCancelled = Number(tcb['flag_cancelled']) === 1;
  const flagApproved = Number(tcb['flag_approved']) === 1;
  const flagRevised = Number(tcb['flag_revised']) === 1;
  const flagClosed = Number(tcb['flag_closed']) === 1;
  const flagPublish = Number(tcb['flag_publish']) === 1;
  const approvedBy = tcb['approved_by'];
  const hasApprovedBy =
    approvedBy != null && approvedBy !== '' && approvedBy !== '00000000-0000-0000-0000-000000000000';

  if (flagCancelled) {
    return 'TCB Rejected';
  }
  if (!flagApproved && hasApprovedBy) {
    return 'Reapproval Pending';
  }
  if (flagRevised) {
    return 'TCB Revised';
  }
  if (flagApproved) {
    return 'TCB Approved';
  }
  if (flagClosed) {
    return 'TCB Closed';
  }
  if (flagPublish) {
    return 'Pending Approval';
  }
  return 'Draft';
}

/** Plain-language hint when Approve / Revise / Reject are disabled due to status. */
export function statusActionDisabledReason(tcbStatus: string): string {
  switch (tcbStatus) {
    case 'TCB Revised':
      return (
        'This TCB was sent for revision. The RM must update it and send for approval again. ' +
        'It will show under Pending when ready.'
      );
    case 'TCB Approved':
      return 'This TCB is already approved.';
    case 'TCB Rejected':
      return 'This TCB was rejected.';
    case 'TCB Closed':
      return 'This TCB is closed.';
    case 'TCB Expired':
      return 'This TCB has expired.';
    case 'Draft':
      return 'This TCB is not published yet. Wait until the RM publishes it for approval.';
    case 'Reapproval Pending':
      return 'Reapproval is pending. You can approve, revise, or reject if Gross Margin / Month (%) is at least 2.5%.';
    case 'Pending Approval':
      return '';
    default:
      return (
        `Approve, revise, and reject are only allowed when status is Pending Approval ` +
        `(current status: ${tcbStatus || 'Unknown'}).`
      );
  }
}

export interface ChActionGate {
  canApprove: boolean;
  canRevise: boolean;
  canReject: boolean;
  showGpActionBar: boolean;
  statusReason: string;
  gpReason: string;
}

/** CH actions: approve only when GM/month % >= threshold; revise/reject when status allows. */
export function resolveChActionGate(
  gpPerMonth: number | string | null | undefined,
  tcbStatus: string
): ChActionGate {
  const statusOk = canActOnTcbStatus(tcbStatus);
  const gpOk = canShowApprovalActions(gpPerMonth);
  const statusReason = statusOk ? '' : statusActionDisabledReason(tcbStatus);
  const gpReason = gpOk ? '' : approvalActionsDisabledReason(gpPerMonth);
  return {
    canApprove: statusOk && gpOk,
    canRevise: statusOk,
    canReject: statusOk,
    showGpActionBar: statusOk,
    statusReason,
    gpReason,
  };
}

/** @deprecated Use resolveChActionGate — true only when approve is allowed. */
export function combineActionGate(
  gpPer: number | string | null | undefined,
  tcbStatus: string
): { allowed: boolean; reason: string } {
  const gate = resolveChActionGate(gpPer, tcbStatus);
  if (!gate.canApprove) {
    return { allowed: false, reason: gate.statusReason || gate.gpReason };
  }
  return { allowed: true, reason: '' };
}

/** Farm GP modal thresholds (approve criteria, not CH list gate). */
export const GP_MIN_GM_PER = 1.2;
export const GP_MIN_NC_MONTH_PER = 0;
export const GP_MIN_GM_MONTH_PER = 1.55;
export const GP_MIN_GM_MONTH_TAL = 1.5;

export function resolveGpGmMonthMin(flagTalTrade: boolean | number | null | undefined): number {
  return Number(flagTalTrade) === 1 ? GP_MIN_GM_MONTH_TAL : GP_MIN_GM_MONTH_PER;
}

/** Admin-style qty display (en-IN, 2 decimals). */
export function formatTcbDetailQty(qty: number | string | null | undefined): string {
  const n = parseFloat(String(qty ?? ''));
  if (isNaN(n)) {
    return String(qty ?? '');
  }
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Material rate column — prefers API rate_uom, else rate/uom_name. */
export function materialRateUom(mat: {
  rate_uom?: string;
  rate?: number | string;
  uom_name?: string;
  uom?: string;
} | null | undefined): string {
  if (!mat) {
    return '';
  }
  if (mat.rate_uom) {
    return String(mat.rate_uom);
  }
  const rate = mat.rate;
  const uom = mat.uom_name || mat.uom;
  if (rate == null || rate === '') {
    return '';
  }
  return uom ? `${rate}/${uom}` : String(rate);
}

export function supplierMaterialsList(supplier: {
  materials?: unknown[];
  msupplier_tcb_materials?: unknown[];
} | null | undefined): unknown[] {
  if (!supplier) {
    return [];
  }
  const mats = supplier.materials ?? supplier.msupplier_tcb_materials;
  return Array.isArray(mats) ? mats : [];
}

/** Farm hides GST Payment On when gst_pmt_on is -1 (no supplier tax). */
export function supplierShowGstPmt(gstPmtOn: number | string | null | undefined): boolean {
  const n = parseInt(String(gstPmtOn ?? ''), 10);
  return !isNaN(n) && n > -1;
}

export function supplierGstPmtLabel(gstPmtOn: number | string | null | undefined): string {
  switch (parseInt(String(gstPmtOn ?? ''), 10)) {
    case 1:
      return 'Advance';
    case 2:
      return 'LR';
    case 3:
      return 'GRN';
    case 4:
      return 'Buyer Payment';
    default:
      return 'N/A';
  }
}

export function supplierGpSectionTitle(supplier: {
  supplier_name?: string;
  gst_pmt_on?: number | string;
}): string {
  const name = supplier.supplier_name || 'Supplier';
  return `${name} - GST PMT ON ${supplierGstPmtLabel(supplier.gst_pmt_on)}`;
}

export function gpMetricMeetsMin(
  value: number | string | null | undefined,
  min: number
): boolean {
  const v = parseFloat(String(value ?? ''));
  return !isNaN(v) && v >= min;
}

/** Progress bar width for threshold metrics (0–100). */
export function gpMetricBarWidth(
  value: number | string | null | undefined,
  min: number
): number {
  const v = parseFloat(String(value ?? ''));
  if (isNaN(v) || min <= 0) {
    return 0;
  }
  if (v >= min) {
    return 100;
  }
  return Math.max(8, Math.min(100, (v / min) * 100));
}

/** Same text as farm tr_tcb admin (#delay_charge). */
export function formatTcbDelayChargesText(tcb: Record<string, unknown> | null | undefined): string {
  if (!tcb || Number(tcb['flag_delay_charges']) !== 1) {
    return 'No Delay Charges Applicable';
  }
  const forCode = parseInt(String(tcb['delay_charges_for'] ?? ''), 10);
  const forLabel = forCode === 1 ? 'Buyer' : 'Supplier';
  const per = parseFloat(String(tcb['delay_charges_per'] ?? ''));
  const perStr = isNaN(per) ? String(tcb['delay_charges_per'] ?? '') : per.toFixed(2);
  return `Delay Charge of ${perStr}% applicable on ${forLabel}.`;
}

export function computeGpTradeVerdict(gp: {
  gp_per?: number | string;
  gp_per_month?: number | string;
  nc_per_month?: number | string;
  flag_tal_trade?: boolean | number;
}): { label: string; tone: 'success' | 'danger' } {
  const gmMonthMin = resolveGpGmMonthMin(gp.flag_tal_trade);
  const reasons: string[] = [];
  if (!gpMetricMeetsMin(gp.gp_per, GP_MIN_GM_PER)) {
    reasons.push('GM% < 1.20%');
  }
  if (!gpMetricMeetsMin(gp.gp_per_month, gmMonthMin)) {
    reasons.push(`GM/Month% < ${gmMonthMin.toFixed(2)}%`);
  }
  if (!gpMetricMeetsMin(gp.nc_per_month, GP_MIN_NC_MONTH_PER)) {
    reasons.push(`NC/Month% < ${GP_MIN_NC_MONTH_PER}%`);
  }
  if (reasons.length === 0) {
    return { label: 'Good to trade', tone: 'success' };
  }
  return { label: `Not good to trade - ${reasons.join(', ')}`, tone: 'danger' };
}
